import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Loader2, Power, RefreshCw, DollarSign, CheckSquare, Square } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AutoReactivatorPage() {
  const [data, setData] = useState({ candidates: [], count: 0, skipped: {}, criteria: {} });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [enablingAll, setEnablingAll] = useState(false);
  const [enablingOne, setEnablingOne] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/reactivator/candidates`);
      setData(res.data);
    } catch (e) {
      toast.error("Błąd: " + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (sid) => {
    const next = new Set(selected);
    if (next.has(sid)) next.delete(sid); else next.add(sid);
    setSelected(next);
  };

  const selectTop = (n) => {
    const ids = data.candidates.slice(0, n).map(c => c.site_id);
    setSelected(new Set(ids));
  };

  const enableOne = async (sid, name) => {
    setEnablingOne(sid);
    try {
      const res = await axios.post(`${API}/admin/reactivator/enable/${sid}`);
      if (res.data.ok) {
        toast.success(`✓ ${name} aktywowany — agent zacznie publikować`);
        await load();
      } else {
        toast.error(`✗ ${name}: ${res.data.error}`);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setEnablingOne(null);
    }
  };

  const enableSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Aktywować ${selected.size} portali? Agent natychmiast zacznie generować content i publikować — wzrośnie zużycie AI tokenów + ad revenue.`)) return;
    setEnablingAll(true);
    try {
      const ids = Array.from(selected);
      // Chunki po 20 max
      let enabled = 0;
      for (let i = 0; i < ids.length; i += 20) {
        const chunk = ids.slice(i, i + 20);
        const res = await axios.post(`${API}/admin/reactivator/bulk-enable`, { site_ids: chunk });
        enabled += res.data.enabled || 0;
      }
      toast.success(`✓ Aktywowano ${enabled} portali`);
      setSelected(new Set());
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setEnablingAll(false);
    }
  };

  // Estymata przyszlej publikacji = candidates × ~5 postow/dzień (avg)
  const projectedPostsPerDay = data.count * 5;
  const projectedRevenuePerMonth = projectedPostsPerDay * 30 * 0.02; // ~0.02 PLN per post via ads (heurystyka)

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Power className="w-8 h-8 text-emerald-600" />
          <div>
            <h1 className="text-2xl font-bold">Auto-Reactivator portali</h1>
            <p className="text-sm text-muted-foreground">
              Zdrowe portale w stand-by — gotowe do reaktywacji. Każdy = +X postów/24h = +ad revenue.
            </p>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          Odśwież
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-emerald-700 uppercase">Kandydatów</p>
            <p className="text-2xl font-bold text-emerald-900">{data.count}</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-blue-700 uppercase">Po wł. wszystkich</p>
            <p className="text-2xl font-bold text-blue-900">+{projectedPostsPerDay}</p>
            <p className="text-xs text-blue-600">postów/24h estymata</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-amber-700 uppercase">Wybrane</p>
            <p className="text-2xl font-bold text-amber-900">{selected.size}</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50/50">
          <CardContent className="p-4">
            <p className="text-xs text-purple-700 uppercase flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Estymata revenue/mc
            </p>
            <p className="text-2xl font-bold text-purple-900">~{projectedRevenuePerMonth.toFixed(0)} PLN</p>
            <p className="text-xs text-purple-600">heurystyka 0.02 zł/post</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200 bg-slate-50/50">
        <CardContent className="p-3 text-xs text-slate-700 flex items-center gap-3 flex-wrap">
          <strong>Filtr:</strong>
          posts ≥ {data.criteria.min_posts} ·
          avg quality ≥ {data.criteria.min_quality} ·
          health OK ·
          ma WP/Emergent creds
          <span className="text-slate-400">|</span>
          <strong>Pominięte:</strong>
          {data.skipped.no_health_ok || 0} bez health ·
          {data.skipped.few_posts || 0} mało postów ·
          {data.skipped.low_quality || 0} słaby content ·
          {data.skipped.no_creds || 0} bez creds ·
          {data.skipped.disabled || 0} disabled
        </CardContent>
      </Card>

      <div className="flex gap-2 flex-wrap items-center">
        <Button size="sm" variant="outline" onClick={() => selectTop(10)}>Top 10</Button>
        <Button size="sm" variant="outline" onClick={() => selectTop(20)}>Top 20</Button>
        <Button size="sm" variant="outline" onClick={() => selectTop(50)}>Top 50</Button>
        <Button size="sm" variant="outline" onClick={() => setSelected(new Set(data.candidates.map(c => c.site_id)))}>
          Wszystkie ({data.count})
        </Button>
        <Button size="sm" variant="outline" onClick={() => setSelected(new Set())}>Wyczyść</Button>
        <div className="ml-auto">
          <Button
            onClick={enableSelected}
            disabled={enablingAll || selected.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {enablingAll ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Power className="w-4 h-4 mr-2" />}
            Aktywuj wybrane ({selected.size})
          </Button>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Kandydaci wg jakości</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="divide-y">
            {data.candidates.map((c, idx) => {
              const isSelected = selected.has(c.site_id);
              return (
                <div key={c.site_id} className={`px-4 py-2 flex items-center gap-3 text-sm ${isSelected ? "bg-emerald-50" : "hover:bg-slate-50"}`}>
                  <button type="button" onClick={() => toggle(c.site_id)} className="shrink-0">
                    {isSelected ? <CheckSquare className="w-4 h-4 text-emerald-600" /> : <Square className="w-4 h-4 text-slate-400" />}
                  </button>
                  <span className="text-xs text-slate-500 w-6">#{idx + 1}</span>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{c.name}</span>
                      <Badge variant="outline" className="text-[10px]">{c.creds}</Badge>
                      <Badge variant="outline" className="text-[10px]">{c.site_type}</Badge>
                    </div>
                    <div className="text-xs text-slate-500 truncate">{c.url}</div>
                  </div>
                  <div className="text-xs text-right">
                    <div>Q: <strong className={c.avg_quality >= 50 ? "text-emerald-700" : "text-amber-700"}>{c.avg_quality}</strong></div>
                    <div className="text-slate-500">{c.n_posts} postów</div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={enablingOne === c.site_id}
                    onClick={() => enableOne(c.site_id, c.name)}
                    className="text-emerald-700 hover:bg-emerald-100"
                  >
                    {enablingOne === c.site_id ? <Loader2 className="w-3 h-3 animate-spin" /> : "Aktywuj →"}
                  </Button>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      <p className="text-xs text-muted-foreground text-center">
        ⚠️ Aktywacja włącza auto_publish=true — agent zacznie generować content (zużycie AI tokenów). Możesz wyłączyć w każdej chwili w /sites.
      </p>
    </div>
  );
}
